import { useCallback, useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import ToolbarButton from '@modules/documents/components/header/toolbar/ToolbarButton';
import { TEMPLATE_FIELD_INLINE_NAME } from '@modules/documents/doc-engine/templateFields';

import { ControlledField } from '@shared/components';

const schema = yup.object({
  cols: yup.number().required('Missing value.').min(1).max(20),
  rows: yup.number().required('Missing value.').min(1).max(100),
  withHeaderRow: yup.boolean(),
});
type TFormData = yup.InferType<typeof schema>;

const useStyles = makeStyles({
  checkbox: {
    marginLeft: '-6px',
  },
});

const TableNodeButton = () => {
  const { editor } = useContext(DocumentContext);

  const [isOpen, setIsOpen] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { cols: 5, rows: 2, withHeaderRow: false },
  });

  const onSubmit = useCallback((data: TFormData) => {
    editor
      .chain()
      .focus()
      .insertTable({ ...data })
      .run();

    setTimeout(reset, 500);
    setIsOpen(false);
  }, []);

  const classes = useStyles();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(event, data) => {
        setIsOpen(data.open);
        setTimeout(reset, 500);
      }}
    >
      <DialogTrigger disableButtonEnhancement>
        <ToolbarButton
          icon={
            <svg height="100%" width="100%" viewBox="0,0,2048,2048" focusable="false">
              <path
                type="path"
                style={{ fill: 'transparent' }}
                d="M 102 102 h 1844 v 1844 h -1844 m 103 -1741 v 205 h 1638 v -205 m -1024 1126 h 410 v -307 h -410 m 410 410 h -410 v 409 h 410 m -512 -512 v -307 h -512 v 307 m 1024 -819 h -410 v 410 h 410 m 102 102 v 307 h 512 v -307 m -512 -102 h 512 v -410 h -512 m -614 0 h -512 v 410 h 512 m -512 512 v 409 h 512 v -409 m 614 409 h 512 v -409 h -512 z"
              ></path>
              <path type="path" style={{ fill: '#FAFAFAFF' }} d="M 1894 1894 h -1740 v -1433 h 1740 z"></path>
              <path type="path" style={{ fill: '#C8C6C4FF' }} d="M 1894 461 h -1740 v -307 h 1740 z"></path>
              <path
                type="path"
                style={{ fill: '#797774FF' }}
                d="M 1894 1024 h -563 v 307 h 563 v 103 h -563 v 460 h -102 v -460 h -410 v 460 h -102 v -460 h -563 v -103 h 563 v -307 h -563 v -102 h 563 v -461 h 102 v 461 h 410 v -461 h 102 v 461 h 563 m -665 409 v -307 h -410 v 307 z"
              ></path>
              <path
                type="path"
                style={{ fill: '#3A3A38FF' }}
                d="M 102 102 h 1844 v 1844 h -1844 m 1741 -103 v -1331 h -1638 v 1331 m 1638 -1433 v -205 h -1638 v 205 z"
              ></path>
            </svg>
          }
          tooltip="Insert Table"
          // Inline fields hold inline content only.
          disabled={(e) => e.isActive(TEMPLATE_FIELD_INLINE_NAME)}
        >
          Table
        </ToolbarButton>
      </DialogTrigger>
      <DialogSurface aria-describedby={undefined}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <DialogTitle>Table</DialogTitle>
            <DialogContent className="flex flex-col gap-2">
              <ControlledField
                label="Number of columns"
                name="cols"
                control={control}
                errors={errors}
                required
                type="number"
              />
              <ControlledField
                label="Number of rows"
                name="rows"
                control={control}
                errors={errors}
                required
                type="number"
              />

              <Controller
                name="withHeaderRow"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    label="With header"
                    checked={field.value}
                    onChange={(e, checked) => field.onChange(checked.checked)}
                    className={classes.checkbox}
                  />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button type="submit" appearance="primary">
                Ok
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default TableNodeButton;
